import { Box, Flex, type FlexProps } from "@chakra-ui/react";
import {
	type FocusEvent,
	type KeyboardEvent,
	useCallback,
	useState,
} from "react";

function DigitCodeInput({
	onKeyDown,
	onFocus,
	onBlur,
	onComplete,
	...props
}: FlexProps & { onComplete: (code: string) => void }) {
	const length = 7;
	const [value, setValue] = useState("");
	const [focus, setFocus] = useState(false);
	const getAt = (index: number) => {
		if (value.length <= index) {
			return "";
		}
		return value[index];
	};
	const isActive = (index: number) => {
		return (
			(index === value.length ||
				(index === length - 1 && value.length === length)) &&
			focus
		);
	};
	const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
		if (event.key === "Backspace") {
			if (value.length > 0) {
				setValue(value.slice(0, value.length - 1));
			}
		} else if (
			["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"].includes(event.key)
		) {
			if (value.length < length) {
				setValue(value + event.key);
				if ((value + event.key).length === length) {
					onComplete(value + event.key);
				}
			}
		}
		if (onKeyDown) {
			onKeyDown(event);
		}
	};

	const handleFocus = useCallback(
		(event: FocusEvent<HTMLDivElement, Element>) => {
			setFocus(true);
			if (onFocus) {
				onFocus(event);
			}
		},
		[setFocus, onFocus],
	);

	const handleBlur = useCallback(
		(event: FocusEvent<HTMLDivElement, Element>) => {
			setFocus(false);
			if (onBlur) {
				onBlur(event);
			}
		},
		[setFocus, onBlur],
	);

	const handleOnPaste = useCallback(
		(event: React.ClipboardEvent<HTMLDivElement>) => {
			const text = event.clipboardData.getData("text/plain");
			setValue(text.slice(0, length));
			if (text.slice(0, length).length === length) {
				onComplete(text.slice(0, length));
			}
		},
		[onComplete],
	);

	return (
		<Flex
			mt={props.mt}
			onPaste={handleOnPaste}
			onKeyDown={handleKeyDown}
			onFocus={handleFocus}
			onBlur={handleBlur}
			tabIndex={0}
			outline={"none"}
		>
			<Box
				border={isActive(0) ? "2px" : "1px"}
				borderColor={isActive(0) ? "brand.700" : "gray.200"}
				borderLeftRadius="xl"
				borderRightRadius="none"
				textAlign="center"
				h="4em"
				lineHeight="4em"
				w="4em"
			>
				{getAt(0)}
			</Box>
			<Box
				border={isActive(1) ? "2px" : "1px"}
				borderLeft={isActive(1) ? "2px" : "none"}
				borderColor={isActive(1) ? "brand.700" : "gray.200"}
				borderRadius="none"
				textAlign="center"
				h="4em"
				lineHeight="4em"
				w="4em"
			>
				{getAt(1)}
			</Box>
			<Box
				border={isActive(2) ? "2px" : "1px"}
				borderLeft={isActive(2) ? "2px" : "none"}
				borderColor={isActive(2) ? "brand.700" : "gray.200"}
				borderRadius="none"
				textAlign="center"
				h="4em"
				lineHeight="4em"
				w="4em"
			>
				{getAt(2)}
			</Box>
			<Box
				border={isActive(3) ? "2px" : "1px"}
				borderLeft={isActive(3) ? "2px" : "none"}
				borderColor={isActive(3) ? "brand.700" : "gray.200"}
				borderRadius="none"
				textAlign="center"
				h="4em"
				lineHeight="4em"
				w="4em"
			>
				{getAt(3)}
			</Box>
			<Box
				border={isActive(4) ? "2px" : "1px"}
				borderLeft={isActive(4) ? "2px" : "none"}
				borderColor={isActive(4) ? "brand.700" : "gray.200"}
				borderRadius="none"
				textAlign="center"
				h="4em"
				lineHeight="4em"
				w="4em"
			>
				{getAt(4)}
			</Box>
			<Box
				border={isActive(5) ? "2px" : "1px"}
				borderLeft={isActive(5) ? "2px" : "none"}
				borderColor={isActive(5) ? "brand.700" : "gray.200"}
				borderRadius="none"
				textAlign="center"
				h="4em"
				lineHeight="4em"
				w="4em"
			>
				{getAt(5)}
			</Box>
			<Box
				border={isActive(6) ? "2px" : "1px"}
				borderLeft={isActive(6) ? "2px" : "none"}
				borderColor={isActive(6) ? "brand.700" : "gray.200"}
				borderLeftRadius="none"
				borderRightRadius="xl"
				textAlign="center"
				h="4em"
				lineHeight="4em"
				w="4em"
			>
				{getAt(6)}
			</Box>
		</Flex>
	);
}

export default DigitCodeInput;
